// Message handling and rendering for CTM Chat Widget
import { formatTime, escapeHtml } from './utils.js';

export class MessageHandler {
  constructor(options = {}, logger) {
    this.logger = logger || console;
    this.messages = [];
    this.messagesContainerEl = null;
    this.currentAIMessageElement = null;
    this.currentAIMessageId = null;
    this.typingIndicatorEl = null;
    this.aiStatusIndicatorEl = null;
    
    // Callbacks
    this.onScrollToBottom = options.onScrollToBottom || (() => {});
    this.onUpdateActiveStatus = options.onUpdateActiveStatus || (() => {});
    this.onDispatchEvent = options.onDispatchEvent || (() => {});
    this.onRenderMarkdown = options.onRenderMarkdown || ((content) => content);
    this.onPlaySound = options.onPlaySound || (() => {});
    this.onFormLoad = options.onFormLoad || (() => {});
    this.onAdjustWindowHeight = options.onAdjustWindowHeight || (() => {});
    this.onUpdateUIBasedOnState = options.onUpdateUIBasedOnState || (() => {});
    this.onSanitizeHTML = options.onSanitizeHTML || ((html) => html);
    this.onFetchFreshMediaUrl = options.onFetchFreshMediaUrl || (() => Promise.reject('Not implemented'));
    
    // Configuration
    this.sessionId = null;
    this.durableObjectId = null;
  }

  setMessagesContainer(container) {
    this.messagesContainerEl = container;
  }

  setSessionInfo(sessionId, durableObjectId) {
    this.sessionId = sessionId;
    this.durableObjectId = durableObjectId;
  }

  addMessage(messageObject, { silent = false } = {}) {
    const noUserOrAiAgentMessages = !this.messages.filter(msg => msg.type !== 'system' && msg.type !== 'status').length;
    if (!this.messagesContainerEl) {
      this.logger.debug("no message container yet");
      return;
    }
    
    // Skip if message ID is currently being streamed
    if (messageObject.id && messageObject.id === this.currentAIMessageId) {
      this.logger.debug("message is streaming");
      return;
    }

    // Avoid adding duplicate messages
    const recentMessages = this.messages.slice(-5);
    const isDuplicate = recentMessages.some(msg => {
      if (messageObject.type === "system" && msg.type === "system") {
        return msg.content === messageObject.content;
      }
      if (msg.type === messageObject.type && msg.content === messageObject.content) {
        if (messageObject.timestamp && msg.timestamp) {
          const timeDiff = Math.abs(new Date(messageObject.timestamp) - new Date(msg.timestamp));
          return timeDiff < 2000; // Within 2 seconds
        }
        return true;
      }
      return false;
    });
    
    if (isDuplicate) {
      this.logger.debug("duplicate message");
      return;
    }

    // Ensure message has content before adding (except for form messages)
    if (!messageObject.content && messageObject.type !== "form") {
      this.logger.debug("no content in message, skipping");
      return;
    }

    this.messages.push(messageObject);
    this.renderSingleMessage(messageObject, { silent });
    this.onScrollToBottom();
    this.onUpdateActiveStatus();

    const addingAiAgentOrUser = messageObject.type === 'ai' || messageObject.type === 'agent' || messageObject.type === 'user';

    if (noUserOrAiAgentMessages && addingAiAgentOrUser) {
      this.onDispatchEvent('start', {
        sessionId: this.sessionId,
        durableObjectId: this.durableObjectId,
        isNewSession: !this.sessionId
      });
    }
    
    // Dispatch 'received' event for incoming messages
    if (messageObject.type === 'ai' || messageObject.type === 'agent') {
      this.onDispatchEvent('received', {
        messageType: messageObject.type,
        content: messageObject.content,
        messageId: messageObject.id,
        hasMedia: !!messageObject.mediaUrl,
        timestamp: messageObject.timestamp || Date.now()
      });
    }
  }

  renderSingleMessage(message, { silent = false } = {}) {
    if (!this.messagesContainerEl) return;
    
    // Map role to type for consistency
    let type = message.type;
    if (!type && message.role) {
      if (message.role === "assistant") type = "ai";
      else if (message.role === "user") type = "user";
      else type = message.role;
    }
    
    if (message.content && message.content.startsWith("ping client:")) {
      return; // Skip ping messages
    }

    // Handle system messages visibility
    if (type === 'system' && message.clientVisible === false) {
      return;
    }

    // Remove indicators before adding a new message bubble unless it's an AI chunk/start
    if (type !== "ai_chunk" && type !== "ai_message_start") {
      this.showTypingIndicator(false);
      this.messagesContainerEl.querySelector(".ai-status-indicator")?.remove();
      this.messagesContainerEl.querySelector(".typing-indicator")?.remove();
    }

    // Create message element
    const messageEl = document.createElement("div");
    messageEl.classList.add("message");
    
    // Map type to CSS class (ai messages use "other" class for styling)
    const typeClass = type === "user" ? "user" : 
                     type === "ai" ? "other" : 
                     type === "agent" ? "other" : 
                     type === "system" ? "system" : 
                     type === "status" ? "status" : 
                     type === "form" ? "form" : "other";
    messageEl.classList.add(typeClass);
    
    // Add welcome class if this is a welcome message
    if (message.welcome) {
      messageEl.classList.add("welcome");
    }
    
    // Use more specific parts for agent/ai if possible
    const specificType = type === "agent" ? "agent" : type === "ai" ? "ai" : typeClass;
    messageEl.setAttribute("part", `message message-${specificType}`);
    messageEl.setAttribute("data-message-id", message.id || "");

    // Add timestamp
    if (message.timestamp && type !== "system" && type !== "status") {
      const timestampEl = document.createElement("div");
      timestampEl.classList.add("timestamp");
      timestampEl.textContent = formatTime(new Date(message.timestamp));
      messageEl.appendChild(timestampEl);
    }

    // Handle form messages
    if (type === 'form' && message.form) {
      this.renderFormInMessage(messageEl, message);
      // Continue to append the element below
    } else if (type === "user" || type === "agent") {

      // Add content for user/agent messages
      const textEl = document.createElement("div");
      textEl.classList.add("text");
      textEl.textContent = message.content;
      messageEl.appendChild(textEl);
    } else if (type === "ai") {
      const textEl = document.createElement("div");
      textEl.classList.add("text");
      textEl.innerHTML = this.onRenderMarkdown(message.content);
      messageEl.appendChild(textEl);
    } else if (type === "system" || type === "status") {
      messageEl.textContent = message.content;
      if (message.temporary) {
        messageEl.classList.add("temporary");
      }
    } else if (type !== "form") {
      // For any other non-form messages, add content as text
      messageEl.textContent = message.content || "";
    }

    // Handle file uploads with preview
    if (message.file && (message.file.url || message.file.preview)) {
      const fileContainer = document.createElement("div");
      fileContainer.style.display = "flex";
      fileContainer.style.flexDirection = "column";
      fileContainer.style.gap = "8px";
      
      // Add file info text
      const fileInfo = document.createElement("div");
      fileInfo.textContent = message.content || `Uploaded: ${message.file.name}`;
      fileContainer.appendChild(fileInfo);
      
      // Add image preview if it's an image
      if (!message.file.type || message.file.type.startsWith("image/")) {
        const img = document.createElement("img");
        const displayUrl = message.file.secureUrl || message.file.url || message.file.preview;
        img.src = displayUrl;
        img.style.maxWidth = "200px";
        img.style.maxHeight = "200px";
        img.style.borderRadius = "8px";
        img.style.cursor = "pointer";
        img.onclick = () => {
          window.open(message.file.secureUrl || message.file.publicUrl || message.file.url || message.file.preview, '_blank');
        };
        fileContainer.appendChild(img);
      }
      
      messageEl.appendChild(fileContainer);
    } else if (message.media && message.media.length > 0) {
      // Handle media messages from agents
      this.renderMediaAttachments(messageEl, message);
    } else if (message.mediaUrl) {
      // Legacy media URL support
      const mediaContainer = document.createElement("div");
      mediaContainer.classList.add("media-container");
      
      const mediaLink = document.createElement("a");
      mediaLink.href = message.mediaUrl;
      mediaLink.target = "_blank";
      mediaLink.rel = "noopener noreferrer";
      
      const mediaImg = document.createElement("img");
      mediaImg.src = message.mediaUrl;
      mediaImg.alt = "Uploaded image";
      mediaImg.style.maxWidth = "200px";
      mediaImg.style.maxHeight = "200px";
      mediaImg.style.borderRadius = "8px";
      mediaImg.style.cursor = "pointer";
      
      mediaLink.appendChild(mediaImg);
      mediaContainer.appendChild(mediaLink);
      messageEl.appendChild(mediaContainer);
    }

    // Add to messages container if not already done
    if (!messageEl.parentNode) {
      this.messagesContainerEl.appendChild(messageEl);
    }

    // Re-append indicators after the message so they always appear below last content
    if (this.aiStatusIndicatorEl && !this.aiStatusIndicatorEl.classList.contains("hidden")) {
      this.messagesContainerEl.appendChild(this.aiStatusIndicatorEl);
    }
    if (this.typingIndicatorEl && !this.typingIndicatorEl.classList.contains("hidden")) {
      this.messagesContainerEl.appendChild(this.typingIndicatorEl);
    }

    // Play sound for incoming live messages (not history replay)
    if (!silent && (type === "ai" || type === "agent") && this.onPlaySound) {
      this.onPlaySound();
    }
  }

  renderFormInMessage(messageEl, message) {
    const form = message.form;
    
    // Create form container
    const formContainer = document.createElement("div");
    formContainer.style.paddingTop = "12px";

    // Create the form iframe
    const iframe = document.createElement("iframe");
    iframe.className = "ctm-call-widget";
    iframe.src = form.url;
    iframe.style.width = "100%";
    iframe.style.border = "none";

    // Get header height from shadowRoot if available
    const shadowRoot = this.messagesContainerEl?.getRootNode();
    const headerHeight = shadowRoot?.querySelector?.('.header')?.offsetHeight || 48;
    const chatWindowHeight = window.innerHeight; // Full screen on mobile
    const availableHeight = chatWindowHeight - headerHeight - 80; // 80px for padding/margins
    
    iframe.style.height = `${Math.max(availableHeight, 400)}px`;
    iframe.style.minHeight = "400px";
    iframe.style.maxHeight = "100%";
    
    formContainer.appendChild(iframe);
    messageEl.appendChild(formContainer);

    // Store form data for later use
    const formId = form.url.split("/").pop().split(".").shift();
    this.onFormLoad(formId, {
      div: messageEl,
      origin: new URL(form.url).origin,
      send_to_ai: message.send_to_ai || false,
      formConfig: message.formConfig,
      visitorSid: message.visitorSid || null,
    });

    // Adjust layout without moving the visitor away from the welcome message
    // or a field they have scrolled to while the form was loading.
    setTimeout(() => {
      this.onAdjustWindowHeight();
      this.onUpdateUIBasedOnState();

      setTimeout(() => {
        this.onAdjustWindowHeight();
      }, 1000);
    }, 100);
    
    // Load form script if needed
    if (!window.__ctm_loader_run) {
      const script = document.createElement("script");
      script.src = form.script;
      messageEl.appendChild(script);
    }
  }

  renderMessages() {
    if (!this.messagesContainerEl) return;
    
    // Clear and re-render all messages
    this.messagesContainerEl.innerHTML = "";
    
    this.messages.forEach(message => {
      this.renderSingleMessage(message, { silent: true });
    });
    
    this.onScrollToBottom();
  }

  createNewAIMessageElement(messageId) {
    if (!this.messagesContainerEl) {
      return;
    }
    
    // Remove any existing AI message element or typing indicator
    this.currentAIMessageElement?.remove();
    this.showTypingIndicator(false);
    
    // Create new AI message container
    const messageEl = document.createElement("div");
    messageEl.classList.add("message", "ai", "streaming");
    messageEl.setAttribute("data-message-id", messageId);
    
    // Add timestamp
    const timestampEl = document.createElement("div");
    timestampEl.classList.add("timestamp");
    timestampEl.textContent = formatTime(new Date());
    messageEl.appendChild(timestampEl);
    
    // Add text container
    const textEl = document.createElement("div");
    textEl.classList.add("text");
    messageEl.appendChild(textEl);
    
    // Add to DOM
    // Add to messages container if not already done
    if (!messageEl.parentNode) {
      this.messagesContainerEl.appendChild(messageEl);
    }
    
    // Store reference
    this.currentAIMessageElement = messageEl;
    this.currentAIMessageId = messageId;
    
    // Add status indicator
    if (!this.aiStatusIndicatorEl) {
      this.aiStatusIndicatorEl = document.createElement("div");
      this.aiStatusIndicatorEl.classList.add("ai-status-indicator");
      this.aiStatusIndicatorEl.innerHTML = '<span class="dot"></span><span class="dot"></span><span class="dot"></span>';
    }
    this.messagesContainerEl.appendChild(this.aiStatusIndicatorEl);
    this.onScrollToBottom();
  }

  updateAIMessage(chunk) {
    if (!this.currentAIMessageElement) return;
    
    const textEl = this.currentAIMessageElement.querySelector(".text");
    if (textEl) {
      // Append chunk to existing content
      const currentContent = textEl.getAttribute('data-raw-content') || '';
      const newContent = currentContent + chunk;
      textEl.setAttribute('data-raw-content', newContent);
      textEl.textContent = newContent;
      this.onScrollToBottom();
    }
  }

  finalizeAIMessage(fullContent, messageId) {
    if (!this.currentAIMessageElement || this.currentAIMessageId !== messageId) return;
    
    // Update content one final time
    const textEl = this.currentAIMessageElement.querySelector(".text");
    if (textEl && fullContent) {
      textEl.innerHTML = this.onRenderMarkdown(fullContent);
    }
    
    // Remove streaming class
    this.currentAIMessageElement.classList.remove("streaming");
    
    // Remove status indicator
    this.aiStatusIndicatorEl?.remove();
    
    // Add the completed message to the messages array
    if (fullContent) {
      const aiMessage = {
        type: "ai",
        role: "assistant",
        content: fullContent,
        id: messageId,
        timestamp: Date.now()
      };
      this.messages.push(aiMessage);
      this.onUpdateActiveStatus();
      
      // Dispatch received event
      this.onDispatchEvent('received', {
        messageType: 'ai',
        content: fullContent,
        messageId: messageId,
        timestamp: aiMessage.timestamp
      });
    }
    
    // Clear references
    this.currentAIMessageElement = null;
    this.currentAIMessageId = null;
    
    this.onScrollToBottom();
  }

  showTypingIndicator(show = true, message = null) {
    if (show) {
      if (!this.typingIndicatorEl) {
        this.typingIndicatorEl = document.createElement("div");
        this.typingIndicatorEl.classList.add("typing-indicator");
        
        const bubble = document.createElement("div");
        bubble.classList.add("typing-bubble");
        bubble.innerHTML = '<span></span><span></span><span></span>';
        
        this.typingIndicatorEl.appendChild(bubble);
      }
      
      if (message) {
        const messageEl = document.createElement("span");
        messageEl.classList.add("typing-message");
        messageEl.textContent = message;
        this.typingIndicatorEl.appendChild(messageEl);
      }
      
      if (!this.typingIndicatorEl.parentNode && this.messagesContainerEl) {
        this.messagesContainerEl.appendChild(this.typingIndicatorEl);
        this.onScrollToBottom();
      }
    } else {
      this.typingIndicatorEl?.remove();
      this.typingIndicatorEl = null;
    }
  }

  clearMessages() {
    this.messages = [];
    this.typingIndicatorEl = null;
    if (this.messagesContainerEl) {
      this.messagesContainerEl.innerHTML = "";
    }
  }

  getMessages() {
    return this.messages;
  }

  filterMessages(predicate) {
    this.messages = this.messages.filter(predicate);
    this.renderMessages();
  }

  renderMediaAttachments(messageEl, message) {
    const mediaContainer = document.createElement("div");
    mediaContainer.style.display = "flex";
    mediaContainer.style.flexDirection = "column";
    mediaContainer.style.gap = "8px";
    
    // Add text content if any
    if (message.content) {
      const textDiv = document.createElement("div");
      textDiv.textContent = message.content;
      mediaContainer.appendChild(textDiv);
    }
    
    // Add media elements
    message.media.forEach(mediaItem => {
      // Handle both string URLs (legacy) and objects with metadata
      const mediaUrl = typeof mediaItem === 'string' ? mediaItem : mediaItem.url;
      const contentType = typeof mediaItem === 'object' ? mediaItem.contentType : 'image/jpeg';
      const filename = typeof mediaItem === 'object' ? mediaItem.filename : 'attachment';
      
      // Check if it's an image
      if (contentType && contentType.startsWith('image/')) {
        const img = document.createElement("img");
        img.src = mediaUrl;
        img.style.maxWidth = "200px";
        img.style.maxHeight = "200px";
        img.style.borderRadius = "8px";
        img.style.cursor = "pointer";
        img.style.marginTop = "8px";
        img.title = filename; // Show filename on hover
        img.onclick = () => {
          window.open(mediaUrl, '_blank');
        };
        img.onerror = async () => {
          // If this is a Twilio media URL that expired, try to fetch a fresh one
          if (mediaUrl.includes('twilio.com') && mediaItem.sid) {
            try {
              const freshUrl = await this.onFetchFreshMediaUrl(mediaItem.sid);
              if (freshUrl) {
                img.src = freshUrl;
                img.onclick = () => {
                  window.open(freshUrl, '_blank');
                };
                return;
              }
            } catch (error) {
              this.logger.error('Failed to fetch fresh media URL:', error);
            }
          }
          
          // If image fails to load, show a link instead
          const link = document.createElement("a");
          link.href = mediaUrl;
          link.target = "_blank";
          link.textContent = `View ${filename}`;
          link.style.color = "#3b82f6";
          img.replaceWith(link);
        };
        mediaContainer.appendChild(img);
      } else {
        // Non-image files show as links
        const link = document.createElement("a");
        link.href = mediaUrl;
        link.target = "_blank";
        link.textContent = `📎 ${filename}`;
        link.style.color = "#3b82f6";
        link.style.display = "block";
        link.style.marginTop = "8px";
        mediaContainer.appendChild(link);
      }
    });
    
    messageEl.appendChild(mediaContainer);
  }
}
